import { isAxiosError } from "axios"

import type { FilthSourceJob, FilthSources } from "@/api/filth-sources-api"

const successfulJobStates = ["completed", "succeeded", "success", "done"]
const failedJobStates = ["failed", "error"]
const terminalJobStates = [...successfulJobStates, ...failedJobStates, "quarantined"]

function isActiveFilthJob(status: string | undefined): boolean {
  return !!status && !terminalJobStates.includes(status)
}

export function hasActiveFilthJobs(data: FilthSources | undefined): boolean {
  return data?.connectors.some((source) => isActiveFilthJob(source.latest_job?.status)) ?? false
}

/** Phases the UI has a translation for; anything else is shown as the raw token. */
const knownJobPhases = [
  "queued",
  "requeued",
  "extracting",
  "discovering",
  "fetching",
  "committing",
  "done",
  "partial",
  "failed",
  "walk",
  "extract",
  "dir_summary",
  "hierarchical_summary",
]

export function filthJobPhaseKey(phase: string | null | undefined): string | null {
  return phase && knownJobPhases.includes(phase) ? phase : null
}

export function filthJobProgress(job: FilthSourceJob | null): number | null {
  if (job?.entities_done == null || job.entities_total == null || job.entities_total <= 0) {
    return null
  }
  return Math.min(100, Math.max(0, (job.entities_done / job.entities_total) * 100))
}

export function filthFinishedAt(timestamp: number | null | undefined, language: string): string {
  if (timestamp == null) return "—"
  const date = new Date(timestamp * 1000)
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(language)
}

export function filthJobBadgeVariant(status: string | undefined) {
  if (failedJobStates.includes(status ?? "")) return "error"
  if (successfulJobStates.includes(status ?? "")) return "success"
  if (isActiveFilthJob(status)) return "warning"
  return "outline"
}

export function filthSourcesError(
  error: unknown
): "unreachable" | "unauthorized" | "unavailable" | "invalid_response" {
  const code: unknown = isAxiosError(error) ? error.response?.data?.error : undefined
  return code === "unreachable" || code === "unauthorized" || code === "invalid_response"
    ? code
    : "unavailable"
}
